import { dateFormat } from '@constants/dateFormat'
import { shifts } from '@constants/orderDispatch'
import { Texto } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import classNames from 'classnames'
import _ from 'lodash'
import moment, { type Moment, type MomentInput } from 'moment'
import { type MutableRefObject, useEffect, useRef } from 'react'

import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { formatTimeWindow } from '../components/_SiteWide/OrderDisplay/OrderTimeFormats'
// TODO: Replace definitions with types in the Gravitate. namespace
import type { Destination, Driver, Event, Sector } from './general.types'

// Aliasing order here because it seems like there's a conflict in usage with the dispatch window fields.
// TODO: Understand true usage and replace in order.d.ts
type Order = Gravitate.Order & {
  dispatch_window_start_date?: Moment | string
  dispatch_window_end_date?: Moment | string
  default_carrier_window_start_local?: Intl.DateTimeFormatOptions['timeZone']
}

export const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max)

/** Selected markets for the global filter, from `localStorage.currentMarket`:
 * a JSON array in the current model, or a legacy single-market string. Empty = all markets. */
export function getCurrentMarkets(): string[] {
  const raw = localStorage.currentMarket
  if (!raw) return []
  const parsed = jsonSafeParse<string[]>(raw)
  // Current model is a JSON array; the legacy model was a single bare string.
  return Array.isArray(parsed) ? parsed.filter(Boolean) : [raw]
}

export function setCurrentMarkets(values: string[]) {
  localStorage.currentMarket = JSON.stringify((values ?? []).filter(Boolean))
}

/** `markets` body params for calls that build their body outside the global API providers. */
export function getMarketFilterParams(): { markets: string[] } {
  return { markets: getCurrentMarkets() }
}

/** Order-insensitive equality for two market-name selections. */
export function marketsEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && JSON.stringify([...a].sort()) === JSON.stringify([...b].sort())
}

export const getShowNetworkTimezone = (args?: { timeZone: Intl.DateTimeFormatOptions['timeZone'] }) => {
  // Added additional optional args param for moment.tz to prevent potential crashes for undefined timezones
  if (args && args.timeZone === undefined) return false
  return localStorage.getItem('USE_NETWORK_TIMEZONE') === 'true'
}

export const setShowNetworkTimezone = (value: boolean) => {
  localStorage.setItem('USE_NETWORK_TIMEZONE', value?.toString() ?? 'false')
}

export const getDisplayTimezoneString = () => {
  return localStorage.getItem('DISPLAY_TIMEZONE_STRING') === 'true'
}

export const setDisplayTimezoneString = (value: boolean) => {
  localStorage.setItem('DISPLAY_TIMEZONE_STRING', value?.toString() ?? 'false')
}

export function getTimeDisplayPreference() {
  const showNetworkTime = localStorage.getItem('USE_NETWORK_TIMEZONE') === 'true'
  const showTimezoneString = localStorage.getItem('DISPLAY_TIMEZONE_STRING') === 'true'
  return showTimezoneString ? 'NETWORK_WITH_TIMEZONE' : showNetworkTime ? 'NETWORK' : 'LOCAL'
}

export type TimeDisplayPreference = ReturnType<typeof getTimeDisplayPreference>

export function set24HourTime(value: boolean) {
  localStorage.setItem('USE_24_HOUR_TIME', value?.toString() ?? 'false')
}

export function get24HourTime() {
  return localStorage.getItem('USE_24_HOUR_TIME') === 'true'
}

export const timePickerFormat = get24HourTime() ? 'HH:mm' : 'hh:mm A'
export const datePickerFormat = get24HourTime() ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY hh:mm A'

export const getNameInitials = (name: string) => {
  return name.indexOf(' ') !== -1
    ? name
        .split(' ')
        .map((n) => n[0])
        .join('')
    : name.substring(0, 1)
}

export const getMarketSectors = (values: string[] = [], sectors: Sector[]) => {
  if (!sectors) return []
  const markets = sectors.map((s) => s.name)
  const sectorList = [] as string[]
  values?.length > 0 &&
    values.forEach((v) => {
      if (markets.includes(v)) {
        const match = sectors.filter((sector) => sector.name.toLowerCase() === v.toLowerCase())
        if (match.length > 0) {
          const marketSectors = match[0].sectors.map((s) => s.name)
          sectorList.push(...marketSectors)
        }
      } else sectorList.push(v)
    })
  return sectorList
}

/** Serialize a filter selection for storage as a JSON array so member values may safely
 * contain commas (a legacy comma-joined string splits such a value apart). */
export function serializeStoredArray(values: string[]): string {
  return JSON.stringify((values ?? []).filter(Boolean))
}

export function parseStoredArray(value: Maybe<string>): string[] {
  if (!value) return []
  if (value.startsWith('[')) {
    const parsed = jsonSafeParse<string[]>(value)
    return Array.isArray(parsed) ? parsed.filter(Boolean) : []
  }
  return value.includes(',') ? value.split(',') : [value]
}

export const titleCase = (string: string) => {
  const words = string.toLowerCase().split('_')
  for (let i = 0; i < words.length; i++) {
    words[i] = words[i][0].toUpperCase() + words[i].slice(1)
  }

  return words.join(' ')
}

export const sortNumeric = (data: string[], property: string, sortDirection = 'asc') => {
  const compareSettings = {
    numeric: true,
    sensitivity: 'base',
  } as Intl.CollatorOptions

  if (!property) {
    return data?.sort((a, b) => a.localeCompare(b, undefined, compareSettings))
  }
  if (sortDirection === 'asc') {
    return data?.sort((a, b) => a[property].localeCompare(b[property], undefined, compareSettings))
  }

  return data?.sort((a, b) => b[property].localeCompare(a[property], undefined, compareSettings))
}

export const sortDestinations = (current: Destination, next: Destination) => {
  const currentLocation = parseInt(current.drops[0].location_name)
  const nextLocation = parseInt(next.drops[0].location_name)

  if (isNaN(currentLocation) && isNaN(nextLocation)) {
    return currentLocation > nextLocation ? 1 : -1
  }

  if (!isNaN(currentLocation) && !isNaN(nextLocation)) {
    return currentLocation > nextLocation ? 1 : -1
  }

  if (isNaN(currentLocation) && !isNaN(nextLocation)) {
    return 1
  }

  if (!isNaN(currentLocation) && isNaN(nextLocation)) {
    return -1
  }
}

// antd column config generator method
export const antDColumns = (
  data: any,
  overrides = {},
  ignoredColumns = [],
  extraColumns = [],
  columnOrder = [],
  explicitColumns = []
) => {
  if (!data || data.length === 0) {
    return []
  }

  const ignore = (ignoredColumns || []).concat(['key'])
  const firstRow = data[0]
  const cols = Object.keys(firstRow)
    .filter((k) => !ignore.includes(k))
    .filter((k) => explicitColumns.length === 0 || explicitColumns.includes(k))
    .map((k) => ({ title: titleCase(k), dataIndex: k, key: k, ...overrides[k] }))
    .concat(extraColumns)
    .map((o) => {
      const orderedIndex = columnOrder.indexOf(o.key)
      return { ...o, order: orderedIndex > -1 ? orderedIndex : undefined }
    })

  return _.orderBy(cols, 'order')
}

export function ReadableDifference(
  dateTo: MomentInput | Dayjs,
  dateFrom: MomentInput | Dayjs = moment().local(),
  forRunout?: boolean,
  isBackhaul?: boolean,
  rightAlign?: boolean
) {
  if (dateTo == null) {
    return '---'
  }

  const dateMoment = dayjs.isDayjs(dateTo)
    ? dateTo.isValid()
      ? moment(dateTo.toDate())
      : null
    : moment(dateTo as MomentInput).local()

  if (!dateMoment || !dateMoment.isValid()) {
    return '---'
  }

  const dateFromMoment = dayjs.isDayjs(dateFrom) ? moment(dateFrom.toDate()) : moment(dateFrom as MomentInput)
  const seconds = dateMoment.diff(dateFromMoment, 'seconds')

  if (forRunout && seconds <= 0) {
    if (isBackhaul) return t('PAST DELIVERY')

    const timeSinceRunout = getTimeSinceRunout(dateMoment, dateFromMoment)

    return (
      <div
        className={classNames('flex-div', { 'items-center': !rightAlign })}
        style={{
          flexWrap: 'wrap',
          justifyContent: rightAlign ? 'flex-end' : '',
          alignItems: rightAlign ? 'flex-end' : '',
        }}
      >
        {/** @ts-ignore */}
        <Texto className='pr-1' appearance='error' weight='bold' align={rightAlign && 'right'}>
          {t('EMPTY')}
        </Texto>
        {/** @ts-ignore */}
        <Texto appearance='error' category='label' align={rightAlign && 'right'}>
          -{timeSinceRunout}
        </Texto>
      </div>
    )
  }
  return humanReadableSecondDifferenceFromToday(seconds)
}

const getTimeSinceRunout = (runoutDate: MomentInput, dateFrom: Moment) => {
  const minutesDiff = dateFrom.diff(runoutDate, 'minutes')
  const hoursDiff = dateFrom.diff(runoutDate, 'hours')

  const numDays = Math.floor(hoursDiff / 24)
  const numHours = Math.floor((minutesDiff % 1440) / 60)
  const numMinutes = Math.floor((minutesDiff % 1440) % 60)
  if (hoursDiff >= 24) {
    return `${numDays}d ${numHours}h ${numMinutes}m`
  }
  if (numHours > 0) {
    return `${numHours}h ${numMinutes}m`
  }
  return `${numMinutes}m`
}

export const getTimeUntilRetain = (retainDate: Moment) => {
  const now = moment()

  // If retainDate is in the past, its ready to go.
  if (now.isSameOrAfter(retainDate)) {
    return 'Fits Now'
  }

  const minutesDiff = retainDate.diff(now, 'minutes')
  const hoursDiff = retainDate.diff(now, 'hours')

  const numDays = Math.floor(hoursDiff / 24)
  const numHours = Math.floor((minutesDiff % 1440) / 60)
  const numMinutes = Math.floor((minutesDiff % 1440) % 60)

  if (hoursDiff >= 24) {
    return `${numDays}d ${numHours}h ${numMinutes}m`
  }
  if (numHours > 0) {
    return `${numHours}h ${numMinutes}m`
  }
  return `${numMinutes}m`
}

export const convertTimeSpanToReadable = (timespan: string) => {
  const a = timespan.split(':')
  const seconds = +a[0] * 60 * 60 + +a[1] * 60 + +a[2]

  return humanReadableSecondDifferenceFromToday(seconds)
}

export const getCarrierDeliveryWindow = (order: Order) => {
  if (!order.carrier_window_start)
    return formatTimeWindow(
      order.default_carrier_window_start,
      order.default_carrier_window_end,
      false,
      false,
      order.default_carrier_window_start_local
    )
  return formatTimeWindow(
    order.carrier_window_start,
    order.carrier_window_end,
    false,
    false,
    order.default_carrier_window_start_local
  )
}

export const filterDriversByHomeLocation = (drivers: Driver[], depots: string[]) => {
  return drivers.filter((d) => depots.includes(d.home_location))
}

export const getEventsFromScheduleData = (scheduleData: any[], shift: string) => {
  const isPm = shift === shifts.PM
  const events = []

  for (let i = 0, dataLength = scheduleData.length; i < dataLength; i++) {
    for (let j = 0, len = scheduleData[i].trips.length; j < len; j++) {
      if (scheduleData[i].trips[j].order_number !== -1) {
        const index = `${i}${j}`
        pushEvent(scheduleData[i].id, index, isPm, scheduleData[i].trips[j], events)
      }
    }
  }

  return events
}

export const formatDateStringToTime = (string: string, timeZone?: Intl.DateTimeFormatOptions['timeZone']) => {
  return formatters.TIME(moment(string), timeZone)
}

export const formatDateStringToReadable = (string: string, timeZone?: Intl.DateTimeFormatOptions['timeZone']) => {
  return formatters.DATE_TIME(moment(string), timeZone)
}

export const isPastForever = (date: MomentInput) => {
  const forever = moment('2100-01-01')
  return moment(date).isAfter(forever)
}

export const humanReadableSecondDifferenceFromToday = (seconds: number) => {
  const showAgo = seconds < 0
  const newSeconds = Math.abs(seconds)

  if (newSeconds < 60) return '0s'

  const numdays = Math.floor(newSeconds / 86400)
  const numhours = Math.floor(((newSeconds % 31536000) % 86400) / 3600)
  const numminutes = Math.floor((((newSeconds % 31536000) % 86400) % 3600) / 60)

  const daysString = numdays !== 0 ? `${Math.abs(numdays)}d ` : ''
  const hoursString = numhours !== 0 ? `${Math.abs(numhours)}h ` : ''
  const minutesString = numminutes > 0 && numdays <= 0 ? `${numminutes.toFixed(0)}m ` : ''

  return `${daysString}${hoursString}${minutesString}${showAgo ? 'ago' : ''}`
}

const translateDateStringToToday = (stringOrDate: string | Date, isPm: boolean) => {
  const pmAdjust = isPm ? 12 : 0

  if (stringOrDate) {
    const dateVersion = stringOrDate instanceof Date ? stringOrDate : new Date(stringOrDate)
    const today = new Date()
    let hoursAdjust = dateVersion.getHours() - pmAdjust

    if (hoursAdjust < 0) {
      hoursAdjust += 24
    }

    today.setHours(hoursAdjust)
    today.setMinutes(dateVersion.getMinutes())
    today.setSeconds(dateVersion.getSeconds())

    return today.toISOString()
  }
}

const pushEvent = (id: string, index: string | number, isPm: boolean, trip: Gravitate.Trip, events: Event[]) => {
  events.push({
    id: `${index}-E`,
    resourceId: `Estimate-${id}`,
    title: String(trip.number),
    start: translateDateStringToToday(trip.estimated_start, isPm),
    end: translateDateStringToToday(trip.estimated_end, isPm),
  })

  if (trip.actual_start_time) {
    events.push({
      id: `${index}-A`,
      resourceId: `Actual-${id}`,
      title: String(trip.number),
      start: translateDateStringToToday(trip.actual_start_time, isPm),
      end: translateDateStringToToday(trip.actual_end_time, isPm),
    })
  }
}

export const useInterval = (callback: Function, delay: number) => {
  const savedCallback = useRef<Function>()

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    const tick = () => {
      if (savedCallback.current) {
        savedCallback.current()
      }
    }
    if (delay) {
      const id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

export const ShowRunOutWarningClass = (estimated_run_out: moment.MomentInput) => {
  const runOutDate = moment(estimated_run_out)
  const tommorrow = moment().utc().add(1, 'd')
  const veryClose = moment().utc().add(6, 'h')
  if (runOutDate <= veryClose) return 'run-out-error'
  if (runOutDate <= tommorrow) return 'run-out-warning'
  return ''
}

export const getTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export const GetAmStartDate = (date: MomentInput) => {
  return moment(date).set({ hour: 4, minute: 30, second: 0, millisecond: 0 }).format(dateFormat.ISO)
}

export const GetAmEndDate = (date: MomentInput) => {
  return moment(date).set({ hour: 16, minute: 30, second: 0, millisecond: 0 }).format(dateFormat.ISO)
}

export const GetPmStartDate = (date: MomentInput) => {
  return moment(date).set({ hour: 16, minute: 30, second: 0, millisecond: 0 }).format(dateFormat.ISO)
}

export const GetPmEndDate = (date: MomentInput) => {
  return moment(date).add(1, 'd').set({ hour: 4, minute: 30, second: 0, millisecond: 0 }).format(dateFormat.ISO)
}

export function roundToNearestHalf(number) {
  return Math.round(number * 2) / 2
}

export const GetDuration = (date: MomentInput) => {
  const now = moment().utc().local()
  const updated = moment.utc(date).local()
  return moment.duration(now.diff(updated))
}

export const TankHasWarning = (estimated_run_out: MomentInput) => {
  const runOutDate = moment.utc(estimated_run_out).local()
  const now = moment().add(1, 'd')
  return runOutDate <= now
}

export const triggerGridRead = (gridReference: MutableRefObject<any>, updatedRecord: any) => {
  if (updatedRecord) {
    gridReference && gridReference.current?.dataSource.updateRow(updatedRecord)
  } else {
    const readFunc = _.get(gridReference, 'current.dataSource.read', () => window.location.reload)
    readFunc()
  }
}

type Maybe<T> = T | undefined | null

export function isDefined<T>(v: Maybe<T>): v is T {
  return typeof v !== 'undefined' && v !== null
}

export function sortStringNumbers(stringOrNumber: (string | number)[]) {
  // Sort strings that contain numbers correctly, ie. sort store numbers
  // storenumbersorter storesort sortstore
  return stringOrNumber.sort((a, b) => {
    const isANumber = typeof a === 'number'
    const isBNumber = typeof b === 'number'

    if (isANumber && isBNumber) {
      return a - b
    }
    if (isANumber) {
      return -1
    }
    if (isBNumber) {
      return 1
    }
    // Both are strings, sort them lexicographically
    return a.toString().localeCompare(b.toString())
  })
}

export function getLocalStorage(key: string) {
  try {
    const value = localStorage.getItem(key)
    if (!value) return null
    return JSON.parse(value)
  } catch (e) {
    console.error('Error parsing grid view list from local storage:', e)
  }
}

export function setLocalStorage<T>(key: string, value: T) {
  if (!value) return null
  try {
    const stringValue = JSON.stringify(value)
    return localStorage.setItem(key, stringValue)
  } catch (e) {
    console.error('Error parsing grid view list from local storage:', e)
  }
}

/**
 *
 * @param jsonString Catch JSON parsing errors and return null if the string is not valid JSON, preventing exceptions from being thrown.
 * @returns Parsed JSON object as type T or null if parsing fails.
 */
export function jsonSafeParse<T>(jsonString: string): T | null {
  try {
    return JSON.parse(jsonString) as T
  } catch (e) {
    console.error('Error parsing JSON string:', e)
    return null
  }
}

export function sortAlphabetically(a: string, b: string) {
  return a.toLowerCase().localeCompare(b.toLowerCase())
}
